import React, { useState, useEffect } from 'react';
import { useApp } from '../context/useApp';
import { motion } from 'framer-motion';
import { 
  BarChart3, LineChart, PieChart, TrendingUp, Users, Calendar, DollarSign, 
  Star, MessageSquare, FileText, Clock, Target, Award, Activity, Filter,
  Download, RefreshCw, Eye, EyeOff, ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import toast from 'react-hot-toast';

const AdvancedAnalytics = () => {
  const { user, language } = useApp();
  const [analytics, setAnalytics] = useState({
    bookings: [],
    ratings: [],
    revenue: [],
    users: [],
    documents: [],
    chats: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const [viewMode, setViewMode] = useState('overview');
  const [showDetails, setShowDetails] = useState(false);

  const translations = {
    english: {
      title: 'Advanced Analytics',
      subtitle: 'Comprehensive insights and performance metrics',
      overview: 'Overview',
      bookings: 'Bookings',
      revenue: 'Revenue',
      users: 'Users',
      performance: 'Performance',
      insights: 'Insights',
      last30Days: 'Last 30 Days',
      last7Days: 'Last 7 Days',
      last90Days: 'Last 90 Days',
      thisYear: 'This Year',
      totalBookings: 'Total Bookings',
      totalRevenue: 'Total Revenue',
      activeUsers: 'Active Users',
      avgRating: 'Average Rating',
      successRate: 'Success Rate',
      responseTime: 'Response Time',
      documentUploads: 'Document Uploads',
      chatMessages: 'Chat Messages',
      topLawyers: 'Top Performing Lawyers',
      topPracticeAreas: 'Top Practice Areas',
      bookingTrends: 'Booking Trends',
      revenueTrends: 'Revenue Trends',
      userGrowth: 'User Growth',
      ratingDistribution: 'Rating Distribution',
      consultationTypes: 'Consultation Types',
      peakHours: 'Peak Hours',
      geographicDistribution: 'Geographic Distribution',
      clientSatisfaction: 'Client Satisfaction',
      lawyerPerformance: 'Lawyer Performance',
      systemMetrics: 'System Metrics',
      exportData: 'Export Data',
      refreshData: 'Refresh Data',
      showDetails: 'Show Details',
      hideDetails: 'Hide Details',
      loading: 'Loading analytics...',
      noData: 'No data available',
      increase: 'Increase',
      decrease: 'Decrease',
      noChange: 'No Change'
    },
    hindi: {
      title: 'उन्नत विश्लेषण',
      subtitle: 'व्यापक अंतर्दृष्टि और प्रदर्शन मेट्रिक्स',
      overview: 'अवलोकन',
      bookings: 'बुकिंग',
      revenue: 'राजस्व',
      users: 'उपयोगकर्ता',
      performance: 'प्रदर्शन',
      insights: 'अंतर्दृष्टि',
      last30Days: 'पिछले 30 दिन',
      last7Days: 'पिछले 7 दिन',
      last90Days: 'पिछले 90 दिन',
      thisYear: 'इस वर्ष',
      totalBookings: 'कुल बुकिंग',
      totalRevenue: 'कुल राजस्व',
      activeUsers: 'सक्रिय उपयोगकर्ता',
      avgRating: 'औसत रेटिंग',
      successRate: 'सफलता दर',
      responseTime: 'प्रतिक्रिया समय',
      documentUploads: 'दस्तावेज़ अपलोड',
      chatMessages: 'चैट संदेश',
      topLawyers: 'शीर्ष प्रदर्शन वाले वकील',
      topPracticeAreas: 'शीर्ष अभ्यास क्षेत्र',
      bookingTrends: 'बुकिंग रुझान',
      revenueTrends: 'राजस्व रुझान',
      userGrowth: 'उपयोगकर्ता वृद्धि',
      ratingDistribution: 'रेटिंग वितरण',
      consultationTypes: 'परामर्श प्रकार',
      peakHours: 'शिखर घंटे',
      geographicDistribution: 'भौगोलिक वितरण',
      clientSatisfaction: 'ग्राहक संतुष्टि',
      lawyerPerformance: 'वकील प्रदर्शन',
      systemMetrics: 'सिस्टम मेट्रिक्स',
      exportData: 'डेटा निर्यात करें',
      refreshData: 'डेटा रिफ्रेश करें',
      showDetails: 'विवरण दिखाएं',
      hideDetails: 'विवरण छिपाएं',
      loading: 'विश्लेषण लोड हो रहा है...',
      noData: 'कोई डेटा उपलब्ध नहीं',
      increase: 'वृद्धि',
      decrease: 'कमी',
      noChange: 'कोई परिवर्तन नहीं'
    }
  };

  const t = translations[language] || translations.english;

  // Load analytics data
  useEffect(() => {
    if (user?.uid) {
      loadAnalyticsData();
    }
  }, [user, timeRange]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Calculate date range
      const now = new Date();
      const startDate = new Date();
      switch (timeRange) {
        case '7d':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(now.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(now.getDate() - 90);
          break;
        case '1y':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          startDate.setDate(now.getDate() - 30);
      }

      // Load bookings data
      const bookingsQuery = query(
        collection(db, 'bookings'),
        where('createdAt', '>=', startDate.toISOString()),
        orderBy('createdAt', 'desc')
      );
      const bookingsSnapshot = await getDocs(bookingsQuery);
      const bookingsData = bookingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Load ratings data
      const ratingsQuery = query(
        collection(db, 'ratings'),
        where('createdAt', '>=', startDate.toISOString()),
        orderBy('createdAt', 'desc')
      );
      const ratingsSnapshot = await getDocs(ratingsQuery);
      const ratingsData = ratingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Load documents data
      const documentsQuery = query(
        collection(db, 'documents'),
        where('uploadedAt', '>=', startDate.toISOString()),
        orderBy('uploadedAt', 'desc')
      );
      const documentsSnapshot = await getDocs(documentsQuery);
      const documentsData = documentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Process and calculate metrics
      const processedData = processAnalyticsData(bookingsData, ratingsData, documentsData);
      setAnalytics(processedData);
    } catch (error) {
      console.error('Error loading analytics data:', error);
      toast.error('Error loading analytics data');
    } finally {
      setLoading(false);
    }
  };

  const processAnalyticsData = (bookings, ratings, documents) => {
    // Calculate key metrics
    const totalBookings = bookings.length;
    const totalRevenue = bookings.reduce((sum, booking) => sum + (booking.totalAmount || 0), 0);
    const avgRating = ratings.length > 0 ? 
      ratings.reduce((sum, rating) => sum + rating.rating, 0) / ratings.length : 0;
    
    // Calculate success rate (completed bookings / total bookings)
    const completedBookings = bookings.filter(b => b.status === 'completed').length;
    const successRate = totalBookings > 0 ? (completedBookings / totalBookings) * 100 : 0;

    // Calculate response time (average time between booking and first response)
    const responseTimes = bookings
      .filter(b => b.firstResponseTime && b.createdAt)
      .map(b => new Date(b.firstResponseTime) - new Date(b.createdAt));
    const avgResponseTime = responseTimes.length > 0 ? 
      responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length : 0;

    // Group data by date for trends
    const bookingsByDate = groupByDate(bookings, 'createdAt');
    const revenueByDate = groupByDate(bookings, 'createdAt', 'totalAmount');
    const ratingsByDate = groupByDate(ratings, 'createdAt');

    // Top performing lawyers
    const lawyerPerformance = calculateLawyerPerformance(bookings, ratings);

    // Top practice areas
    const practiceAreas = calculatePracticeAreas(bookings);

    // Consultation types distribution
    const consultationTypes = calculateConsultationTypes(bookings);

    // Peak hours analysis
    const peakHours = calculatePeakHours(bookings);

    return {
      metrics: {
        totalBookings,
        totalRevenue,
        avgRating: parseFloat(avgRating.toFixed(2)),
        successRate: parseFloat(successRate.toFixed(2)),
        avgResponseTime: Math.round(avgResponseTime / (1000 * 60 * 60)), // Convert to hours
        documentUploads: documents.length,
        activeUsers: new Set(bookings.map(b => b.userId)).size
      },
      trends: {
        bookings: bookingsByDate,
        revenue: revenueByDate,
        ratings: ratingsByDate
      },
      insights: {
        topLawyers: lawyerPerformance,
        topPracticeAreas: practiceAreas,
        consultationTypes,
        peakHours
      },
      rawData: {
        bookings,
        ratings,
        documents
      }
    };
  };

  const groupByDate = (data, dateField, valueField = null) => {
    const grouped = {};
    data.forEach(item => {
      const date = new Date(item[dateField]).toLocaleDateString();
      if (!grouped[date]) {
        grouped[date] = valueField ? 0 : [];
      }
      if (valueField) {
        grouped[date] += item[valueField] || 0;
      } else {
        grouped[date].push(item);
      }
    });
    return grouped;
  };

  const calculateLawyerPerformance = (bookings, ratings) => {
    const lawyerStats = {};
    
    // Calculate booking counts and revenue per lawyer
    bookings.forEach(booking => {
      const lawyerId = booking.lawyerId;
      if (!lawyerStats[lawyerId]) {
        lawyerStats[lawyerId] = {
          name: booking.lawyerName || 'Unknown',
          bookings: 0,
          revenue: 0,
          ratings: [],
          avgRating: 0
        };
      }
      lawyerStats[lawyerId].bookings++;
      lawyerStats[lawyerId].revenue += booking.totalAmount || 0;
    });

    // Calculate average ratings per lawyer
    ratings.forEach(rating => {
      const lawyerId = rating.lawyerId;
      if (lawyerStats[lawyerId]) {
        lawyerStats[lawyerId].ratings.push(rating.rating);
      }
    });

    // Calculate average ratings
    Object.values(lawyerStats).forEach(lawyer => {
      lawyer.avgRating = lawyer.ratings.length > 0 ? 
        lawyer.ratings.reduce((sum, rating) => sum + rating, 0) / lawyer.ratings.length : 0;
    });

    // Sort by revenue and return top 10
    return Object.values(lawyerStats)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
  };

  const calculatePracticeAreas = (bookings) => {
    const areas = {};
    bookings.forEach(booking => {
      const area = booking.specialization || booking.practiceArea || 'General';
      areas[area] = (areas[area] || 0) + 1;
    });
    return Object.entries(areas)
      .map(([area, count]) => ({ area, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  const calculateConsultationTypes = (bookings) => {
    const types = {};
    bookings.forEach(booking => {
      const type = booking.consultationType || booking.type || 'General';
      types[type] = (types[type] || 0) + 1;
    });
    return Object.entries(types)
      .map(([type, count]) => ({ type, count }));
  };

  const calculatePeakHours = (bookings) => {
    const hours = {};
    bookings.forEach(booking => {
      const hour = new Date(booking.createdAt).getHours();
      hours[hour] = (hours[hour] || 0) + 1;
    });
    return Object.entries(hours)
      .map(([hour, count]) => ({ hour: parseInt(hour), count }))
      .sort((a, b) => a.hour - b.hour);
  };

  const exportData = () => {
    const dataStr = JSON.stringify(analytics, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics-${timeRange}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('Analytics data exported successfully!');
  };

  const MetricCard = ({ title, value, icon, change, changeType = 'neutral' }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-sm p-6"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {typeof value === 'number' && value >= 1000 ? 
              (value / 1000).toFixed(1) + 'K' : value}
          </p>
          {change && (
            <div className="flex items-center mt-2">
              {changeType === 'increase' && <ArrowUp className="w-4 h-4 text-green-500" />}
              {changeType === 'decrease' && <ArrowDown className="w-4 h-4 text-red-500" />}
              {changeType === 'neutral' && <Minus className="w-4 h-4 text-gray-500" />}
              <span className={`text-sm ml-1 ${
                changeType === 'increase' ? 'text-green-600' :
                changeType === 'decrease' ? 'text-red-600' : 'text-gray-600'
              }`}>
                {change}% {changeType === 'increase' ? t.increase : 
                          changeType === 'decrease' ? t.decrease : t.noChange}
              </span>
            </div>
          )}
        </div>
        <div className="p-3 bg-blue-50 rounded-lg">
          {icon}
        </div>
      </div>
    </motion.div>
  );

  const ChartCard = ({ title, children, className = "" }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-lg shadow-sm p-6 ${className}`}
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </motion.div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
              <p className="text-gray-600 mt-1">{t.subtitle}</p>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="7d">{t.last7Days}</option>
                <option value="30d">{t.last30Days}</option>
                <option value="90d">{t.last90Days}</option>
                <option value="1y">{t.thisYear}</option>
              </select>
              <button
                onClick={exportData}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <Download className="w-5 h-5" />
                {t.exportData}
              </button>
              <button
                onClick={loadAnalyticsData}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="w-5 h-5" />
                {t.refreshData}
              </button>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <MetricCard
            title={t.totalBookings}
            value={analytics.metrics?.totalBookings || 0}
            icon={<Calendar className="w-6 h-6 text-blue-600" />}
            change={12.5}
            changeType="increase"
          />
          <MetricCard
            title={t.totalRevenue}
            value={`₹${(analytics.metrics?.totalRevenue || 0).toLocaleString()}`}
            icon={<DollarSign className="w-6 h-6 text-green-600" />}
            change={8.2}
            changeType="increase"
          />
          <MetricCard
            title={t.avgRating}
            value={`${analytics.metrics?.avgRating || 0}/5`}
            icon={<Star className="w-6 h-6 text-yellow-600" />}
            change={2.1}
            changeType="increase"
          />
          <MetricCard
            title={t.successRate}
            value={`${analytics.metrics?.successRate || 0}%`}
            icon={<Target className="w-6 h-6 text-purple-600" />}
            change={-1.5}
            changeType="decrease"
          />
        </div>

        {/* Charts and Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartCard title={t.bookingTrends}>
            <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center">
                <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Booking trends chart will be displayed here</p>
              </div>
            </div>
          </ChartCard>

          <ChartCard title={t.revenueTrends}>
            <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center">
                <LineChart className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Revenue trends chart will be displayed here</p>
              </div>
            </div>
          </ChartCard>
        </div>

        {/* Top Performers and Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <ChartCard title={t.topLawyers}>
            <div className="space-y-3">
              {analytics.insights?.topLawyers?.slice(0, 5).map((lawyer, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{lawyer.name}</p>
                      <p className="text-sm text-gray-500">{lawyer.bookings} bookings</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">₹{lawyer.revenue.toLocaleString()}</p>
                    <p className="text-sm text-gray-500">{lawyer.avgRating.toFixed(1)}★</p>
                  </div>
                </div>
              ))}
            </div>
          </ChartCard>

          <ChartCard title={t.topPracticeAreas}>
            <div className="space-y-3">
              {analytics.insights?.topPracticeAreas?.slice(0, 5).map((area, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-green-600">{index + 1}</span>
                    </div>
                    <p className="font-medium text-gray-900">{area.area}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{area.count}</p>
                    <p className="text-sm text-gray-500">bookings</p>
                  </div>
                </div>
              ))}
            </div>
          </ChartCard>
        </div>

        {/* Additional Insights */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <ChartCard title={t.consultationTypes}>
            <div className="space-y-2">
              {analytics.insights?.consultationTypes?.map((type, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{type.type}</span>
                  <span className="text-sm font-medium text-gray-900">{type.count}</span>
                </div>
              ))}
            </div>
          </ChartCard>

          <ChartCard title={t.peakHours}>
            <div className="h-48 flex items-center justify-center bg-gray-50 rounded-lg">
              <div className="text-center">
                <Activity className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Peak hours chart will be displayed here</p>
              </div>
            </div>
          </ChartCard>

          <ChartCard title={t.systemMetrics}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t.avgResponseTime}</span>
                <span className="text-sm font-medium text-gray-900">
                  {analytics.metrics?.avgResponseTime || 0}h
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t.documentUploads}</span>
                <span className="text-sm font-medium text-gray-900">
                  {analytics.metrics?.documentUploads || 0}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">{t.activeUsers}</span>
                <span className="text-sm font-medium text-gray-900">
                  {analytics.metrics?.activeUsers || 0}
                </span>
              </div>
            </div>
          </ChartCard>
        </div>
      </div>
    </div>
  );
};

export default AdvancedAnalytics;
